import { useState, useEffect, useCallback, SetStateAction, useMemo } from 'react'
import { addCartItem, addMultipleCartItems } from '../helpers/cart'
import { globalEmitter, emitterProps } from '../helpers/emitter'
import { openModal } from '../helpers/useModal'
import { OmneoList, OmneoListItem, OmneoListItemMeta, type_WISHLIST_DASHBOARD_STATE } from '../types'
import { ChevronLeftIcon } from '../icons/ChevronLeftIcon'
import { ShareThinIcon } from '../icons/ShareThinIcon'
import { ThreeDotIcon } from '../icons/ThreeDotIcon'
import { Modal } from './Modal'
import { SelectInput } from './SelectInput'
import { FormValidate } from './FormValidate'
import { TextInput } from './TextInput'
import { TextAreaInput } from './TextAreaInput'
import { type_GM_STATE } from '../../helpers'
import { WishlistCard } from './WishlistCard'
import { WishlistSettingList } from './WishlistSettingList'
import { OmneoListItemInStore } from '../types/omneo-wishlist-item'

declare var GM_STATE: type_GM_STATE
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE
declare var FB: any // facebook SDK

export function WishlistDetailHeader ({ lists, currentList, setPage }: { lists: OmneoList[], currentList: OmneoList, setPage: React.Dispatch<React.SetStateAction<string>>}) {
  const openShareModal = async () => {
    const modalId = 'modal-share'
    openModal(modalId)
  }

  useEffect(() => {
    if (!currentList.is_shared) {
      sessionStorage.setItem('show-share-private', 'true')
    }
  }, [])

  const backToList = () => {
    const urlParams = new URL(window.location.href)
    urlParams.hash = '#view=lists'
    const existingHash = new URLSearchParams(urlParams.hash)
    const listIndex = existingHash.get('listIndex')
    urlParams.hash = `#view=lists${listIndex ? `&listIndex=${listIndex}` : ''}`
    window.history.pushState({}, '', urlParams)

    setPage('list')
  }

  const listPrivacy = [
    {
      "name": "Private",
      "value": "private"
    },
    {
      "name": "Public",
      "value": "public"
    }
  ]

  const openSettingListModal = () => {
    openModal('setting-list-slideout')
  }

  const showMessageError = (message: string) => {
    globalEmitter.emit('triggerNotification', { message, type: 'error' })
  }
  
  const showMessSuccess = (message: string) => {
    globalEmitter.emit('triggerNotification', { message, type: 'success' })
  }
  
  const handleChangeWishlistStatus = async (status: string) => {
    const isShare = status === 'public'
  
    if (isShare === currentList.is_shared) return
  
    try {
      const name = currentList.name
      if (isShare) {
        await window.omneoService.shareWishlist(currentList.id)
      } else {
        await window.omneoService.deleteWishlistShare(currentList.id, currentList.shares[0]?.id || 0)
      }
  
      const list = await window.omneoService.updateList(currentList.id, name)
  
      if (list) {
        showMessSuccess('Update list successfully')
        globalEmitter.emit('updateList', { list })
      }
    } catch (error) {
      console.log(error)
      showMessageError('Update list failed')
    }
  }
  

  return (
    <div className="relative bg-background-light">
      <button className='cursor-pointer flex items-center text-ink absolute top-0 left-0 px-5 py-[30px] z-10 md-max:pt-5 md-max:pb-0' onClick={backToList}>
        <ChevronLeftIcon className="text-ink" />
        <span className='ml-2.5 text-sm-b font-light font-calibre text-ink uppercase'>
          {wishlistDashboardState.backToWishlists}
        </span>
      </button>
      {currentList.items.length >= 0 && (
        <>
          <div className="flex justify-between px-5 pt-[93px] pb-5 md-max:flex-col md:items-center md:pt-[120px] md:pb-[30px]">
            <h1 className="mb-0 text-3xl font-light tracking-normal uppercase text-ink">{currentList.name}</h1>
            <div className='flex items-center md-max:justify-between md-max:mt-[30px]'>
              <SelectInput
                id="wishlist_privacy"
                label="Privacy"
                name="privacy"
                value={currentList.is_shared ? 'public' : 'private'}
                items={listPrivacy}
                wrapClass="mb-0"
                customClass="h-fit w-[76px] py-[5px] text-sm-b font-light uppercase font-calibre text-ink border-b-0 bg-[right_top_8px]"
                labelClass="hidden"
                onChange={handleChangeWishlistStatus}
              />
              {currentList.is_shared && <button className='relative flex items-center ml-[30px]' onClick={openShareModal}>
                <ShareThinIcon className="text-ink" />
                <span className='pl-2.5 text-sm-b uppercase text-ink font-calibre font-light'>{wishlistDashboardState.share}</span>
              </button>}
              <button onClick={openSettingListModal} className='w-4 h-4 ml-[30px]'>
                <ThreeDotIcon className="text-grey-700" />
              </button>
            </div>
          </div>

          <RenderShareContent currentList={currentList} />
          <RenderShareEmailContent />
          <WishlistSettingList lists={lists} currentList={currentList} />
        </>
      )}
    </div>
  )
}

export function WishlistDetailContent ({ currentList }: { currentList: OmneoList }) {
  const { items } = currentList
  const [wishlistItems, setWishlistItems] = useState(items)

  return wishlistItems.length > 0 ? (
    <RenderWishlistItems currentList={currentList} items={wishlistItems} setItems={setWishlistItems} />
  ) : (
    <RenderWishlistEmpty />
  )
}

function RenderWishlistItems ({ currentList, items, setItems }: { currentList: OmneoList, items: Array<OmneoListItem | OmneoListItemInStore>, setItems: React.Dispatch<React.SetStateAction<OmneoListItem[]>> }) {
  const [currentItem, setCurrentItem] = useState<OmneoListItemMeta | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentIndex, setcurrentIndex] = useState<number | null>(null)

  const removeItem = async (item: OmneoListItem) => {
    try {
      const res = await window.omneoService.removeItemFromList(currentList.handle, item.id)
      if (res) {
        const newItems = items.filter((i: OmneoListItem) => i.id !== item.id)
        setItems(newItems)
        globalEmitter.emit('triggerNotification', { message: 'Item removed successfully', type: 'success' })
        globalEmitter.emit(emitterProps.wishlist.remove)
      } else {
        console.error('error')
        globalEmitter.emit('triggerNotification', { message: 'Item could not be removed to wishlist', type: 'error' })
      }
    } catch (error) {
      console.error('error', error)
      globalEmitter.emit('triggerNotification', { message: 'Item could not be removed to wishlist', type: 'error' })
    }
  }

  const addToBag = async (item: OmneoListItemMeta, index: number) => {
    setCurrentItem(item)
    setIsLoading(true)
    setcurrentIndex(index)

    try {
      if (item.properties) {
        const properties = {
          items: item.properties.map((item: any) => {
            const newItem = { ...item };

            if (newItem.properties.hasOwnProperty('_engravedProduct')) {
              newItem.properties._engravedProduct = Date.now();
            }
            if (newItem.properties.hasOwnProperty('_uniqueTimeStamp')) {
              newItem.properties._uniqueTimeStamp = Date.now();
            }
            if (newItem.properties.hasOwnProperty('_uniqueItem')) {
              newItem.properties._uniqueItem = Date.now();
            }
            if (Array.isArray(newItem.properties) && newItem.properties.length === 0) {
              newItem.properties = {}
            }
    
            return newItem;
          })
        }

        const rest = await addMultipleCartItems(properties)
        if (rest) {
          globalEmitter.emit('product:added', { quantity: 1, variant: { name: rest.items.find((i: any) => i.id === item.variantId)?.title || rest.items[0]?.title } })
        }
      } else {
        const properties = {
          _uniqueTimeStamp: Date.now()
        }
        const res = await addCartItem(item.variantId, properties)
        if (res) {
          globalEmitter.emit('product:added', { quantity: 1, variant: { name: res.items[0]?.title } })
        }
      }
    } catch (e) {
      console.error('error', e)
      globalEmitter.emit('triggerNotification', { message: e?.message, type: 'error' })
    }
    setCurrentItem(null)
    setIsLoading(false)
  }

  return (
    <div className='grid grid-cols-2 gap-2.5 px-5 md:grid-cols-3 md:gap-5 lg:grid-cols-4'>
      {items
        .filter((item: OmneoListItem | OmneoListItemInStore) => !item.meta.hidden)
        .map((item: OmneoListItem | OmneoListItemInStore) => (
          <WishlistCard
            key={item.id}
            item={item}
            removeItem={removeItem}
            addToBag={addToBag}
            loading={isLoading}
            currentVariantId={currentItem?.variantId || null}
            allowRemoveFromList={true}
            wishlistType={currentList.definition.handle}
          />
      ))}
    </div>
  )
}

function RenderWishlistEmpty () {
  const emptyState = GM_STATE.wishlist.empty

  return (
    <div className="flex justify-center items-center flex-col py-20 min-h-[658px] md-max:px-5 md-max:py-[60px] md-max:mt-8 md-max:min-h-[597px]">
      <div className='text-center text-ink'>
        <h3 className='mb-0 h3 max-w-[350px] md:max-w-[478px]'>{emptyState.title}</h3>
        <p className='mt-[15px] text-sm-c font-light font-sans md:mt-5'>{emptyState.subtext}</p>
      </div>
      <a href={emptyState.linkEmptyList} className='Button Button--tertiary w-full max-w-[478px] mx-auto mt-[30px] md:mt-[45px]'>{emptyState.ctaText}</a>
    </div>
  )
}

function RenderShareContent ({ currentList }: { currentList: OmneoList }) {
  const [isCopy, setIsCopy] = useState(false)
  const publicWishlistLink = useMemo(
    () =>
      currentList.is_shared
        ? `${location.origin}/pages/${GM_STATE.wishlist.sharedWishlist.page}?handle=${currentList.shares?.[0]?.handle}`
        : '',
    [currentList]
  )

  useEffect(() => {
    if (!FB || FB === undefined) {
      console.error("FB is not defined.");
      return;
    }

    FB?.XFBML?.parse()
  }, [])

  const handleGetLink = useCallback(() => {
    setIsCopy(true)
    // cannot open this slideout unless this is defined, so should not be an issue 
    navigator.clipboard.writeText(publicWishlistLink)
    .catch(err => console.error('Could not copy', err))
    setTimeout(() => {
      setIsCopy(false)
    }, 5000)
  }, [publicWishlistLink])

  const contentSharePublic = () => {
    return (
      <div className='w-full'>
        <h2 className='mb-[30px] text-xl font-light uppercase text-ink tracking-normal text-center md:mb-[45px] md:text-3xl'>{wishlistDashboardState.sharePublic.title}</h2>
        <button
          className='w-full button-secondary-dark font-calibre'
          onClick={handleGetLink}
        >
          {isCopy
            ? wishlistDashboardState.sharePublic.copied
            : wishlistDashboardState.sharePublic.copy}
        </button>
      </div>
    )
  }

  return (
    <Modal
      id={'modal-share'}
      className="modal-share-public"
      content={currentList.is_shared ? contentSharePublic() : <></>}
      type={'slideout'}
      heading={wishlistDashboardState.wishlist}
    />
  )
}

function RenderShareEmailContent () {
  const [senderName, setSenderName] = useState('')
  const [recipientsEmail, setRecipientsEmail] = useState('')
  const [message, setMessage] = useState('')

  const submitForm = (formEl: HTMLFormElement | null) => {
    console.log('formEl', formEl)
  }

  const contentShareEmail = () => {
    return (
      <div className="flex flex-col items-center justify-center w-full">
        <h2 className='mb-[15px] text-xl font-light uppercase text-ink tracking-normal text-center md:mb-5 md:text-3xl'>{wishlistDashboardState.sharePublic.sendEmail}</h2>
        <p className='mb-[30px] text-sm-c font-light text-ink font-calibre md:mb-[45px]'>{wishlistDashboardState.sharePublic.yourInformation}</p>
        <FormValidate id={'wishlist-share-public'} submitForm={submitForm} className='w-full'>
          <TextInput
            id="wishlist-recipients-email"
            type="email"
            label={wishlistDashboardState.sharePublic.recipientsEmail}
            name="email"
            placeholder={wishlistDashboardState.sharePublic.recipientsEmail}
            required={true}
            classWrap="mb-2.5"
            onInput={(e: { target: { value: SetStateAction<string>; }; }) => setRecipientsEmail(e.target.value)}
          />
          <TextInput
            id="wishlist-sender-name"
            type="text"
            label={wishlistDashboardState.sharePublic.senderName}
            name="name"
            placeholder={wishlistDashboardState.sharePublic.senderName}
            required={true}
            classWrap="mb-2.5"
            onInput={(e: { target: { value: SetStateAction<string>; }; }) => setSenderName(e.target.value)}
          />
          <TextAreaInput
            id="wishlist-message"
            label={wishlistDashboardState.sharePublic.message}
            name="note"
            placeholder={wishlistDashboardState.sharePublic.message}
            classWrap="mb-[30px] [&>.input-label]:hidden md:mb-[45px]"
            onInput={(e: { target: { value: SetStateAction<string>; }; }) => setMessage(e.target.value)}
          />
          <button className="w-full button-tertiary">{wishlistDashboardState.sharePublic.send}</button>
        </FormValidate>
      </div>
    )
  }
  return (
    <Modal
      id={'modal-email'}
      className="modal-email-share"
      content={ contentShareEmail()}
      type={'slideout'}
      heading={wishlistDashboardState.wishlist}
    />
  )
}